import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { ArrowLeft, Send, Plus, Clock, User, Wrench, Trash2, Euro, CalendarDays, Mic, MicOff, Loader2, Package, ScanBarcode, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useVoiceRecognition } from '../hooks/useVoiceRecognition';
import { QuickConvertButton } from '../components/QuickWorkflow';
import { PhotoCapture } from '../components/PhotoCapture';
import { SignaturePad } from '../components/SignaturePad';
import { parseApiError } from '../lib/parseApiError';
import { formatDate } from '../lib/formatDate';
import { uid } from '../lib/uid';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITA = ['bassa', 'media', 'alta', 'urgente'];
const STATI = ['aperto', 'in_lavorazione', 'chiuso', 'annullato'];

export default function TicketDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin, isTecnico } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [cliente, setCliente] = useState(null);
  const [impianto, setImpianto] = useState(null);
  const [tecnici, setTecnici] = useState([]);
  const [interventi, setInterventi] = useState([]);
  const [articoli, setArticoli] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nota, setNota] = useState('');
  const [interventoDialogOpen, setInterventoDialogOpen] = useState(false);
  const [interventoForm, setInterventoForm] = useState({
    descrizione: '',
    data_intervento: new Date().toISOString().split('T')[0],
    ora_inizio: '',
    durata_ore: '',
    note: '',
    righe: [{ descrizione: '', costo: '', _uid: uid() }],
    materiali_usati: []
  });
  
  // Voice input states
  const [voiceDialogOpen, setVoiceDialogOpen] = useState(false);
  const [voiceMode, setVoiceMode] = useState('nota'); // 'nota' o 'intervento'
  
  // Use centralized voice recognition hook
  const {
    isListening,
    isSupported: voiceSupported,
    transcript: voiceText,
    startListening,
    stopListening,
    resetTranscript,
    setTranscript: setVoiceText
  } = useVoiceRecognition({ lang: 'it-IT', continuous: true, showToasts: false });
  
  // Barcode scanner state
  const [barcodeInput, setBarcodeInput] = useState('');
  const [barcodeScanMode, setBarcodeScanMode] = useState(false);
  const barcodeInputRef = useRef(null);

  // Gestione righe dettagli intervento
  const addDettaglio = () => {
    setInterventoForm({
      ...interventoForm,
      righe: [...interventoForm.righe, { descrizione: '', costo: '', _uid: uid() }]
    });
  };

  const updateDettaglio = (index, field, value) => {
    const newRighe = [...interventoForm.righe];
    newRighe[index][field] = value;
    setInterventoForm({ ...interventoForm, righe: newRighe });
  };

  const removeDettaglio = (index) => {
    if (interventoForm.righe.length > 1) {
      setInterventoForm({
        ...interventoForm,
        righe: interventoForm.righe.filter((_, i) => i !== index)
      });
    }
  };

  const getTotaleIntervento = () => {
    const totaleRighe = interventoForm.righe.reduce((sum, d) => sum + (parseFloat(d.costo) || 0), 0);
    const totaleMateriali = interventoForm.materiali_usati.reduce((sum, m) => sum + (m.quantita * m.prezzo_unitario), 0);
    return totaleRighe + totaleMateriali;
  };

  // Gestione materiali
  const addMateriale = () => {
    setInterventoForm({
      ...interventoForm,
      materiali_usati: [...interventoForm.materiali_usati, { 
        articolo_id: '', 
        articolo_codice: '', 
        articolo_descrizione: '',
        quantita: 1, 
        prezzo_unitario: 0,
        _uid: uid()
      }]
    });
  };

  const updateMateriale = (index, field, value) => {
    const newMateriali = [...interventoForm.materiali_usati];
    newMateriali[index][field] = value;
    
    // Se cambia l'articolo, aggiorna anche codice, descrizione e prezzo
    if (field === 'articolo_id') {
      const articolo = articoli.find(a => a.id === value);
      if (articolo) {
        newMateriali[index].articolo_codice = articolo.codice;
        newMateriali[index].articolo_descrizione = articolo.descrizione;
        newMateriali[index].prezzo_unitario = articolo.prezzo_vendita || 0;
      }
    }
    
    setInterventoForm({ ...interventoForm, materiali_usati: newMateriali });
  };

  const removeMateriale = (index) => {
    setInterventoForm({
      ...interventoForm,
      materiali_usati: interventoForm.materiali_usati.filter((_, i) => i !== index)
    });
  };

  // Barcode scanner handler
  const handleBarcodeScan = (e) => {
    if (e.key === 'Enter' && barcodeInput.trim()) {
      e.preventDefault();
      const codice = barcodeInput.trim().toUpperCase();
      
      // Cerca l'articolo per codice
      const articolo = articoli.find(a => 
        a.codice.toUpperCase() === codice || 
        a.codice_barre?.toUpperCase() === codice
      );
      
      if (articolo) {
        // Verifica se l'articolo è già nella lista
        const existingIndex = interventoForm.materiali_usati.findIndex(m => m.articolo_id === articolo.id);
        
        if (existingIndex >= 0) {
          // Incrementa la quantità
          const newMateriali = [...interventoForm.materiali_usati];
          newMateriali[existingIndex].quantita += 1;
          setInterventoForm({ ...interventoForm, materiali_usati: newMateriali });
          toast.success(`${articolo.codice}: quantità aumentata a ${newMateriali[existingIndex].quantita}`);
        } else {
          // Aggiungi nuovo materiale
          const nuovoMateriale = {
            articolo_id: articolo.id,
            articolo_codice: articolo.codice,
            articolo_descrizione: articolo.descrizione,
            quantita: 1,
            prezzo_unitario: articolo.prezzo_vendita || 0,
            _uid: uid()
          };
          setInterventoForm({
            ...interventoForm,
            materiali_usati: [...interventoForm.materiali_usati, nuovoMateriale]
          });
          toast.success(`Aggiunto: ${articolo.codice} - ${articolo.descrizione}`);
        }
      } else {
        toast.error(`Articolo non trovato: ${codice}`);
      }
      
      setBarcodeInput('');
      // Mantieni il focus sull'input barcode
      barcodeInputRef.current?.focus();
    }
  };

  const toggleBarcodeScanMode = () => {
    setBarcodeScanMode(!barcodeScanMode);
    if (!barcodeScanMode) {
      // Attiva modalità scansione
      setTimeout(() => barcodeInputRef.current?.focus(), 100);
      toast.info('Modalità scansione attiva - Scansiona i codici a barre');
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [ticketRes, tecniciRes, interventiRes, articoliRes] = await Promise.all([
        axios.get(`${API}/tickets/${id}`),
        axios.get(`${API}/tecnici`),
        axios.get(`${API}/interventi?ticket_id=${id}`),
        axios.get(`${API}/magazzino/articoli`).catch(() => ({ data: [] }))
      ]);
      setTicket(ticketRes.data);
      setTecnici(tecniciRes.data);
      setInterventi(interventiRes.data);
      setArticoli(articoliRes.data);
      
      if (ticketRes.data.cliente_id) {
        const clienteRes = await axios.get(`${API}/clienti/${ticketRes.data.cliente_id}`);
        setCliente(clienteRes.data);
      }
      
      if (ticketRes.data.impianto_id) {
        const impiantoRes = await axios.get(`${API}/impianti/${ticketRes.data.impianto_id}`);
        setImpianto(impiantoRes.data);
      }
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpdateTicket = async (updates) => {
    try {
      await axios.put(`${API}/tickets/${id}`, updates);
      toast.success('Ticket aggiornato');
      fetchData();
    } catch (error) {
      toast.error(parseApiError(error, 'Errore aggiornamento'));
    }
  };

  const handleAddNota = async () => {
    if (!nota.trim()) return;
    try {
      await axios.post(`${API}/tickets/${id}/nota?messaggio=${encodeURIComponent(nota)}`);
      toast.success('Nota aggiunta');
      setNota('');
      fetchData();
    } catch (error) {
      toast.error(parseApiError(error, 'Errore'));
    }
  };

  const handleAddIntervento = async (e) => {
    e.preventDefault();
    try {
      // Filtra solo le righe con descrizione compilata
      const righeValide = interventoForm.righe
        .filter(d => d.descrizione.trim())
        .map(d => ({
          descrizione: d.descrizione,
          costo: d.costo ? parseFloat(d.costo) : 0
        }));
      
      // Filtra materiali validi
      const materialiValidi = interventoForm.materiali_usati
        .filter(m => m.articolo_id && m.quantita > 0)
        .map(m => ({
          articolo_id: m.articolo_id,
          articolo_codice: m.articolo_codice,
          articolo_descrizione: m.articolo_descrizione,
          quantita: parseFloat(m.quantita),
          prezzo_unitario: parseFloat(m.prezzo_unitario) || 0
        }));
      
      await axios.post(`${API}/interventi`, {
        ticket_id: id,
        descrizione: interventoForm.descrizione,
        data_intervento: interventoForm.data_intervento,
        ora_inizio: interventoForm.ora_inizio || null,
        durata_ore: parseFloat(interventoForm.durata_ore) || 0,
        note: interventoForm.note || null,
        righe: righeValide,
        materiali_usati: materialiValidi
      });
      toast.success('Intervento registrato' + (materialiValidi.length > 0 ? ' - Materiali scalati dal magazzino' : ''));
      setInterventoDialogOpen(false);
      setInterventoForm({
        descrizione: '',
        data_intervento: new Date().toISOString().split('T')[0],
        ora_inizio: '',
        durata_ore: '',
        note: '',
        righe: [{ descrizione: '', costo: '', _uid: uid() }],
        materiali_usati: []
      });
      fetchData();
    } catch (error) {
      toast.error(parseApiError(error, 'Errore'));
    }
  };

  const getTecnicoName = (tecnicoId) => {
    const tecnico = tecnici.find(t => t.id === tecnicoId);
    return tecnico ? `${tecnico.nome} ${tecnico.cognome}` : '-';
  };

  // ===== VOICE INPUT FUNCTIONS =====
  
  const openVoiceInput = (mode) => {
    if (!voiceSupported) {
      toast.error('Il tuo browser non supporta il riconoscimento vocale. Usa Chrome o Edge.');
      return;
    }
    
    setVoiceMode(mode);
    resetTranscript();
    setVoiceDialogOpen(true);
    startListening();
    
    const msg = mode === 'nota' 
      ? 'Parla ora... Dettami la nota per questo ticket'
      : 'Parla ora... Descrivi l\'intervento effettuato';
    toast.info(msg);
  };
  
  const handleStopVoice = () => {
    stopListening();
  };
  
  const restartVoice = () => {
    resetTranscript();
    startListening();
  };
  
  // Helper per parsing data vocale (es: "oggi", "domani", "15 marzo")
  const parseVoiceDate = (text) => {
    const textLower = text.toLowerCase();
    let date = new Date();
    
    if (textLower.includes('oggi')) {
      // già oggi
    } else if (textLower.includes('ieri')) {
      date.setDate(date.getDate() - 1);
    } else if (textLower.includes('domani')) {
      date.setDate(date.getDate() + 1);
    }
    
    // Cerca data esplicita (es: "15 marzo")
    const mesi = ['gennaio','febbraio','marzo','aprile','maggio','giugno','luglio','agosto','settembre','ottobre','novembre','dicembre'];
    const matchGiornoMese = textLower.match(/(\d{1,2})\s*(gennaio|febbraio|marzo|aprile|maggio|giugno|luglio|agosto|settembre|ottobre|novembre|dicembre)/);
    if (matchGiornoMese) {
      const giorno = parseInt(matchGiornoMese[1]);
      const meseNome = matchGiornoMese[2];
      const mese = mesi.indexOf(meseNome);
      if (mese >= 0) {
        date = new Date(date.getFullYear(), mese, giorno);
      }
    }
    
    // Formato YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const confirmVoiceInput = async () => {
    if (!voiceText.trim()) {
      toast.error('Nessun testo da salvare');
      return;
    }
    
    if (voiceMode === 'nota') {
      // Aggiungi nota
      try {
        await axios.post(`${API}/tickets/${id}/nota?messaggio=${encodeURIComponent(voiceText)}`);
        toast.success('Nota vocale aggiunta');
        fetchData();
      } catch (error) {
        toast.error(parseApiError(error, 'Errore'));
      }
    } else if (voiceMode === 'intervento-descrizione') {
      // Aggiorna descrizione nel form intervento
      const parsedDate = parseVoiceDate(voiceText);
      setInterventoForm(prev => ({
        ...prev,
        descrizione: voiceText,
        data_intervento: parsedDate
      }));
      toast.success('Descrizione aggiunta');
    } else if (voiceMode === 'intervento-note') {
      // Aggiorna note nel form intervento
      setInterventoForm(prev => ({
        ...prev,
        note: prev.note ? prev.note + ' ' + voiceText : voiceText
      }));
      toast.success('Note aggiunte');
    }
    
    // Chiudi dialog vocale
    cancelVoiceInput();
  };
  
  const cancelVoiceInput = () => {
    stopListening();
    setVoiceDialogOpen(false);
    resetTranscript();
  };
  
  // ===== END VOICE INPUT FUNCTIONS =====

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground font-mono text-sm">CARICAMENTO...</div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Ticket non trovato</p>
        <Link to="/tickets">
          <Button variant="outline" className="mt-4 rounded-none">Torna ai tickets</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="ticket-detail">
      <div className="flex items-center gap-4">
        <Link to="/tickets">
          <Button variant="ghost" size="icon" className="rounded-none">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-black tracking-tight">#{ticket.numero}</h1>
            <Badge className={`badge-status badge-${ticket.stato} rounded-none`}>
              {ticket.stato?.replace('_', ' ')}
            </Badge>
            <span className={`priority-${ticket.priorita} uppercase text-xs font-mono`}>
              {ticket.priorita}
            </span>
          </div>
          <p className="text-muted-foreground text-sm mt-1">{ticket.oggetto}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Descrizione */}
          <Card className="rounded-none">
            <CardHeader>
              <CardTitle className="text-sm font-mono uppercase tracking-wider">Descrizione</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm whitespace-pre-wrap">{ticket.descrizione}</p>
            </CardContent>
          </Card>

          {/* Cronologia */}
          <Card className="rounded-none">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-mono uppercase tracking-wider">Cronologia</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-64">
                <div className="space-y-4">
                  {/* Creazione */}
                  <div className="flex gap-3 text-sm border-l-2 border-border pl-4">
                    <div className="flex-1">
                      <p className="font-medium">Ticket creato</p>
                      <p className="text-xs text-muted-foreground font-mono">
                        {new Date(ticket.created_at).toLocaleString('it-IT')}
                      </p>
                    </div>
                  </div>
                  
                  {/* Modifiche */}
                  {ticket.modifiche?.map((mod) => (
                    <div key={mod.id} className="flex gap-3 text-sm border-l-2 border-primary pl-4">
                      <div className="flex-1">
                        <p className="font-medium">{mod.messaggio}</p>
                        <p className="text-xs text-muted-foreground">
                          <span className="font-mono">{new Date(mod.data).toLocaleString('it-IT')}</span>
                          {' - '}{mod.utente_nome}
                        </p>
                      </div>
                    </div>
                  ))}
                  
                  {/* Chiusura */}
                  {ticket.closed_at && (
                    <div className="flex gap-3 text-sm border-l-2 border-success pl-4">
                      <div className="flex-1">
                        <p className="font-medium text-success">Ticket chiuso</p>
                        <p className="text-xs text-muted-foreground font-mono">
                          {new Date(ticket.closed_at).toLocaleString('it-IT')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </ScrollArea>
              
              {/* Aggiungi nota - sempre visibile per permettere note anche dopo riapertura */}
              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex gap-2">
                  <Textarea
                    placeholder="Aggiungi una nota..."
                    value={nota}
                    onChange={(e) => setNota(e.target.value)}
                    className="rounded-none min-h-20"
                    data-testid="ticket-nota-input"
                  />
                  <div className="flex flex-col gap-1">
                    <Button 
                      onClick={handleAddNota} 
                      size="icon" 
                      className="rounded-none h-9"
                      disabled={!nota.trim()}
                      data-testid="ticket-add-nota-btn"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                    <Button 
                      onClick={() => openVoiceInput('nota')} 
                      size="icon" 
                      variant="outline"
                      className="rounded-none h-9 border-cyan-500 text-cyan-600"
                      data-testid="voice-nota-btn"
                    >
                      <Mic className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Interventi */}
          <Card className="rounded-none">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-mono uppercase tracking-wider flex items-center gap-2">
                <Wrench className="h-4 w-4" />
                Interventi ({interventi.length})
              </CardTitle>
              {(isTecnico() || isAdmin()) && ticket.stato !== 'chiuso' && (
                <div className="flex gap-2">
                  {/* Quick Workflow Button */}
                  <QuickConvertButton 
                    ticket={{...ticket, cliente_nome: cliente?.ragione_sociale}}
                    tecnici={tecnici}
                    size="sm"
                    onComplete={fetchData}
                  />
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="rounded-md font-mono uppercase text-xs border-cyan-500 text-cyan-600"
                    onClick={() => {
                      // Naviga al calendario con i dati precompilati
                      const params = new URLSearchParams({
                        ticket_id: ticket.id,
                        ticket_numero: ticket.numero,
                        cliente_id: ticket.cliente_id || '',
                        cliente_nome: cliente?.ragione_sociale || '',
                        oggetto: ticket.oggetto,
                        tecnico_id: ticket.tecnico_assegnato_id || ''
                      });
                      navigate(`/calendario?${params.toString()}`);
                    }}
                    data-testid="fissa-appuntamento-btn"
                  >
                    <CalendarDays className="h-3 w-3 mr-1" />
                    Fissa Appuntamento
                  </Button>
                  <Dialog open={interventoDialogOpen} onOpenChange={setInterventoDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="rounded-none font-mono uppercase text-xs" data-testid="new-intervento-btn">
                        <Plus className="h-3 w-3 mr-1" />
                        Nuovo Intervento
                      </Button>
                    </DialogTrigger>
                  <DialogContent className="rounded-none sm:max-w-2xl">
                    <DialogHeader>
                      <DialogTitle className="font-mono uppercase tracking-wider">Nuovo Intervento</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddIntervento} className="space-y-4">
                      <div>
                        <Label className="text-xs font-mono uppercase">Descrizione Generale *</Label>
                        <div className="flex gap-2">
                          <Textarea
                            value={interventoForm.descrizione}
                            onChange={(e) => setInterventoForm({...interventoForm, descrizione: e.target.value})}
                            className="rounded-none flex-1"
                            required
                            placeholder="Descrizione sintetica dell'intervento"
                            data-testid="intervento-descrizione"
                          />
                          <Button 
                            type="button"
                            onClick={() => openVoiceInput('intervento-descrizione')} 
                            size="icon" 
                            variant="outline"
                            className="rounded-none h-20 border-cyan-500 text-cyan-600"
                            data-testid="voice-intervento-descrizione-btn"
                          >
                            <Mic className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label className="text-xs font-mono uppercase">Data *</Label>
                          <Input
                            type="date"
                            value={interventoForm.data_intervento}
                            onChange={(e) => setInterventoForm({...interventoForm, data_intervento: e.target.value})}
                            className="rounded-none"
                            required
                            data-testid="intervento-data"
                          />
                        </div>
                        <div>
                          <Label className="text-xs font-mono uppercase">Ora Inizio</Label>
                          <Input
                            type="time"
                            value={interventoForm.ora_inizio}
                            onChange={(e) => setInterventoForm({...interventoForm, ora_inizio: e.target.value})}
                            className="rounded-none"
                            data-testid="intervento-ora"
                          />
                        </div>
                        <div>
                          <Label className="text-xs font-mono uppercase">Durata (ore)</Label>
                          <Input
                            type="number"
                            step="0.5"
                            value={interventoForm.durata_ore}
                            onChange={(e) => setInterventoForm({...interventoForm, durata_ore: e.target.value})}
                            className="rounded-none"
                            data-testid="intervento-durata"
                          />
                        </div>
                      </div>
                      
                      {/* Dettagli intervento con righe multiple */}
                      <div className="border border-border p-4">
                        <div className="flex justify-between items-center mb-3">
                          <Label className="text-xs font-mono uppercase flex items-center gap-2">
                            <Euro className="h-4 w-4" />
                            Dettagli Intervento / Costi
                          </Label>
                          <Button 
                            type="button" 
                            variant="outline" 
                            size="sm" 
                            className="rounded-none text-xs"
                            onClick={addDettaglio}
                            data-testid="add-riga-intervento"
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Aggiungi Riga
                          </Button>
                        </div>
                        
                        <div className="space-y-2">
                          {interventoForm.righe.map((dettaglio, index) => (
                            <div key={dettaglio._uid || `riga-${index}`} className="flex gap-2 items-start">
                              <div className="flex-1">
                                <Input
                                  value={dettaglio.descrizione}
                                  onChange={(e) => updateDettaglio(index, 'descrizione', e.target.value)}
                                  className="rounded-none text-sm"
                                  placeholder="Descrizione (es: Sostituzione testina)"
                                  data-testid={`dettaglio-desc-${index}`}
                                />
                              </div>
                              <div className="w-32">
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={dettaglio.costo}
                                  onChange={(e) => updateDettaglio(index, 'costo', e.target.value)}
                                  className="rounded-none text-sm font-mono"
                                  placeholder="EUR"
                                  data-testid={`dettaglio-costo-${index}`}
                                />
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-9 w-9 rounded-none text-destructive"
                                onClick={() => removeDettaglio(index)}
                                disabled={interventoForm.righe.length === 1}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Materiali da Magazzino */}
                      <div className="border border-border p-4 bg-muted/30">
                        <div className="flex justify-between items-center mb-3">
                          <Label className="text-xs font-mono uppercase flex items-center gap-2">
                            <Package className="h-4 w-4" />
                            Materiali da Magazzino (scarico automatico)
                          </Label>
                          <div className="flex gap-2">
                            <Button 
                              type="button" 
                              variant={barcodeScanMode ? "default" : "outline"}
                              size="sm" 
                              className="rounded-none text-xs"
                              onClick={toggleBarcodeScanMode}
                              data-testid="barcode-scan-btn"
                            >
                              <ScanBarcode className="h-3 w-3 mr-1" />
                              {barcodeScanMode ? 'Scansione Attiva' : 'Scansiona Barcode'}
                            </Button>
                            <Button 
                              type="button" 
                              variant="outline" 
                              size="sm" 
                              className="rounded-none text-xs"
                              onClick={addMateriale}
                              data-testid="add-materiale-btn"
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Manuale
                            </Button>
                          </div>
                        </div>

                        {/* Barcode Scanner Input */}
                        {barcodeScanMode && (
                          <div className="mb-4 p-3 bg-primary/10 border border-primary/30 rounded-sm">
                            <div className="flex items-center gap-2 mb-2">
                              <ScanBarcode className="h-5 w-5 text-primary animate-pulse" />
                              <Label className="text-sm font-medium text-primary">
                                Modalità Scansione Attiva
                              </Label>
                            </div>
                            <Input
                              ref={barcodeInputRef}
                              type="text"
                              value={barcodeInput}
                              onChange={(e) => setBarcodeInput(e.target.value)}
                              onKeyDown={handleBarcodeScan}
                              placeholder="Scansiona il codice a barre o digita il codice..."
                              className="rounded-none font-mono text-lg h-12"
                              autoFocus
                              data-testid="barcode-input"
                            />
                            <p className="text-xs text-muted-foreground mt-2">
                              Punta il lettore barcode qui. Dopo ogni scansione, l'articolo viene aggiunto automaticamente.
                              Scansiona lo stesso codice per aumentare la quantità.
                            </p>
                          </div>
                        )}
                        
                        {interventoForm.materiali_usati.length === 0 && !barcodeScanMode ? (
                          <p className="text-xs text-muted-foreground text-center py-2">
                            Nessun materiale aggiunto. Usa "Scansiona Barcode" o "Manuale" per aggiungere materiali.
                          </p>
                        ) : interventoForm.materiali_usati.length === 0 && barcodeScanMode ? (
                          <p className="text-xs text-muted-foreground text-center py-2">
                            Inizia a scansionare i codici a barre degli articoli...
                          </p>
                        ) : (
                          <div className="space-y-2">
                            {interventoForm.materiali_usati.map((materiale, index) => {
                              const articoloSelezionato = articoli.find(a => a.id === materiale.articolo_id);
                              return (
                                <div key={materiale._uid || `mat-${index}`} className="flex gap-2 items-start bg-background p-2 border border-border">
                                  <div className="flex-1">
                                    <Select 
                                      value={materiale.articolo_id || "none"} 
                                      onValueChange={(value) => updateMateriale(index, 'articolo_id', value === "none" ? "" : value)}
                                    >
                                      <SelectTrigger className="rounded-none text-sm" data-testid={`materiale-select-${index}`}>
                                        <SelectValue placeholder="Seleziona articolo..." />
                                      </SelectTrigger>
                                      <SelectContent className="rounded-none max-h-60">
                                        <SelectItem value="none">-- Seleziona --</SelectItem>
                                        {articoli.map((a) => (
                                          <SelectItem key={a.id} value={a.id}>
                                            <span className="font-mono">{a.codice}</span> - {a.descrizione} 
                                            <span className="text-muted-foreground ml-2">
                                              (disp: {a.quantita} {a.unita_misura})
                                            </span>
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    {articoloSelezionato && (
                                      <p className="text-xs text-muted-foreground mt-1">
                                        Disponibile: {articoloSelezionato.quantita} {articoloSelezionato.unita_misura} | 
                                        Prezzo: {articoloSelezionato.prezzo_vendita?.toFixed(2) || '0.00'} €
                                      </p>
                                    )}
                                  </div>
                                  <div className="w-20">
                                    <Input
                                      type="number"
                                      min="0.01"
                                      step="0.01"
                                      value={materiale.quantita}
                                      onChange={(e) => updateMateriale(index, 'quantita', parseFloat(e.target.value) || 0)}
                                      className="rounded-none text-sm font-mono"
                                      placeholder="Qtà"
                                      data-testid={`materiale-qty-${index}`}
                                    />
                                  </div>
                                  <div className="w-24">
                                    <Input
                                      type="number"
                                      step="0.01"
                                      value={materiale.prezzo_unitario}
                                      onChange={(e) => updateMateriale(index, 'prezzo_unitario', parseFloat(e.target.value) || 0)}
                                      className="rounded-none text-sm font-mono"
                                      placeholder="€/pz"
                                      data-testid={`materiale-prezzo-${index}`}
                                    />
                                  </div>
                                  <div className="w-24 text-right">
                                    <span className="font-mono text-sm font-bold">
                                      {(materiale.quantita * materiale.prezzo_unitario).toFixed(2)} €
                                    </span>
                                  </div>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-9 w-9 rounded-none text-destructive"
                                    onClick={() => removeMateriale(index)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                        
                        {getTotaleIntervento() > 0 && (
                          <div className="border border-border p-3 bg-success/5 flex justify-between items-center">
                            <span className="text-sm font-mono text-muted-foreground">TOTALE INTERVENTO:</span>
                            <span className="font-mono font-bold text-xl text-success">{getTotaleIntervento().toFixed(2)} EUR</span>
                          </div>
                        )}
                      
                      <div>
                        <Label className="text-xs font-mono uppercase">Note</Label>
                        <div className="flex gap-2">
                          <Textarea
                            value={interventoForm.note}
                            onChange={(e) => setInterventoForm({...interventoForm, note: e.target.value})}
                            className="rounded-none flex-1"
                            placeholder="Note aggiuntive..."
                            data-testid="intervento-note"
                          />
                          <Button 
                            type="button"
                            onClick={() => openVoiceInput('intervento-note')} 
                            size="icon" 
                            variant="outline"
                            className="rounded-none h-20 border-cyan-500 text-cyan-600"
                            data-testid="voice-intervento-note-btn"
                          >
                            <Mic className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Foto intervento (pre/post) */}
                      <div className="border-t border-border pt-3">
                        <PhotoCapture
                          photos={interventoForm.foto || []}
                          onChange={(p) => setInterventoForm({ ...interventoForm, foto: p })}
                          max={6}
                          label="Foto intervento (pre/post)"
                        />
                      </div>

                      {/* Firma del cliente */}
                      <div className="border-t border-border pt-3">
                        <SignaturePad
                          value={interventoForm.firma_cliente}
                          onChange={(sig) => setInterventoForm({ ...interventoForm, firma_cliente: sig })}
                          label="Firma del cliente (tablet/touchscreen)"
                        />
                      </div>

                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={() => setInterventoDialogOpen(false)} className="rounded-none">
                          Annulla
                        </Button>
                        <Button type="submit" className="rounded-none font-mono uppercase" data-testid="save-intervento-btn">
                          Salva Intervento
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {interventi.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nessun intervento registrato</p>
              ) : (
                <div className="space-y-3">
                  {interventi.map((int) => {
                    const totaleRighe = int.righe?.reduce((sum, d) => sum + (d.costo || 0), 0) || 0;
                    const totaleMateriali = int.materiali_usati?.reduce((sum, m) => sum + (m.quantita * m.prezzo_unitario), 0) || 0;
                    const totale = totaleRighe + totaleMateriali || int.costo_totale || 0;
                    return (
                      <div key={int.id} className="border border-border p-4 text-sm">
                        <div className="flex justify-between items-start mb-2">
                          <p className="font-medium">{int.descrizione}</p>
                          {totale > 0 && (
                            <Badge variant="outline" className="rounded-none font-mono bg-success/10 text-success">
                              {totale.toFixed(2)} EUR
                            </Badge>
                          )}
                        </div>
                        <div className="flex gap-4 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDate(int.data_intervento)} {int.ora_inizio && `alle ${int.ora_inizio}`} - {int.durata_ore}h
                          </span>
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {getTecnicoName(int.tecnico_id)}
                          </span>
                        </div>
                        {/* Dettagli intervento */}
                        {int.righe && int.righe.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-border">
                            <p className="text-xs font-mono uppercase text-muted-foreground mb-2">Dettagli:</p>
                            <div className="space-y-1">
                              {int.righe.map((det, idx) => (
                                <div key={`${det.descrizione}-${idx}`} className="flex justify-between text-xs">
                                  <span>{det.descrizione}</span>
                                  <span className="font-mono">{det.costo?.toFixed(2) || '0.00'} EUR</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* Materiali usati */}
                        {int.materiali_usati && int.materiali_usati.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-border">
                            <p className="text-xs font-mono uppercase text-muted-foreground mb-2 flex items-center gap-1">
                              <Package className="h-3 w-3" />
                              Materiali da Magazzino:
                            </p>
                            <div className="space-y-1">
                              {int.materiali_usati.map((mat, idx) => (
                                <div key={`${mat.articolo_id || mat.descrizione}-${idx}`} className="flex justify-between text-xs bg-muted/50 p-1 px-2">
                                  <span>
                                    <span className="font-mono">{mat.articolo_codice}</span> - {mat.articolo_descrizione}
                                    <span className="text-muted-foreground ml-2">x{mat.quantita}</span>
                                  </span>
                                  <span className="font-mono">{(mat.quantita * mat.prezzo_unitario).toFixed(2)} EUR</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {int.note && <p className="mt-2 text-muted-foreground italic">{int.note}</p>}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Info Cliente */}
          <Card className="rounded-none">
            <CardHeader>
              <CardTitle className="text-sm font-mono uppercase tracking-wider">Cliente</CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              {cliente ? (
                <div className="space-y-2">
                  <Link to={`/clienti/${cliente.id}`} className="font-medium hover:underline">
                    {cliente.ragione_sociale}
                  </Link>
                  {cliente.telefono && <p className="text-muted-foreground">{cliente.telefono}</p>}
                  {cliente.email && <p className="text-muted-foreground">{cliente.email}</p>}
                </div>
              ) : (
                <p className="text-muted-foreground">-</p>
              )}
            </CardContent>
          </Card>

          {/* Impianto */}
          {impianto && (
            <Card className="rounded-none">
              <CardHeader>
                <CardTitle className="text-sm font-mono uppercase tracking-wider">Impianto</CardTitle>
              </CardHeader>
              <CardContent className="text-sm">
                <p className="font-mono">{impianto.matricola}</p>
                <p className="text-muted-foreground">{impianto.tipo}</p>
                <p className="text-muted-foreground">{impianto.marca} {impianto.modello}</p>
              </CardContent>
            </Card>
          )}

          {/* Azioni */}
          <Card className="rounded-none">
            <CardHeader>
              <CardTitle className="text-sm font-mono uppercase tracking-wider">Azioni</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs font-mono uppercase">Stato</Label>
                <Select 
                  value={ticket.stato} 
                  onValueChange={(value) => handleUpdateTicket({ stato: value })}
                >
                  <SelectTrigger className="rounded-none" data-testid="update-stato">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {STATI.map((s) => (
                      <SelectItem key={s} value={s} className="capitalize">{s.replace('_', ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(ticket.stato === 'chiuso' || ticket.stato === 'annullato') && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Cambia stato per riaprire il ticket
                  </p>
                )}
              </div>
              
              {ticket.stato !== 'chiuso' && ticket.stato !== 'annullato' && (
                <>
                  <div>
                    <Label className="text-xs font-mono uppercase">Priorità</Label>
                    <Select 
                      value={ticket.priorita} 
                      onValueChange={(value) => handleUpdateTicket({ priorita: value })}
                    >
                      <SelectTrigger className="rounded-none" data-testid="update-priorita">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {PRIORITA.map((p) => (
                          <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-xs font-mono uppercase">Tecnico Assegnato</Label>
                    <Select 
                      value={ticket.tecnico_assegnato_id || 'none'} 
                      onValueChange={(value) => handleUpdateTicket({ tecnico_assegnato_id: value === 'none' ? null : value })}
                    >
                      <SelectTrigger className="rounded-none" data-testid="update-tecnico">
                        <SelectValue placeholder="Non assegnato" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        <SelectItem value="none">Non assegnato</SelectItem>
                        {tecnici.map((t) => (
                          <SelectItem key={t.id} value={t.id}>{t.nome} {t.cognome}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Dialog Input Vocale */}
      <Dialog open={voiceDialogOpen} onOpenChange={(open) => !open && cancelVoiceInput()}>
        <DialogContent className="max-w-lg rounded-none">
          <DialogHeader>
            <DialogTitle className="font-black flex items-center gap-2">
              <Mic className="h-5 w-5 text-cyan-500" />
              {voiceMode === 'nota' ? 'Nota Vocale' : 
               voiceMode === 'intervento-descrizione' ? 'Descrizione Intervento' :
               voiceMode === 'intervento-note' ? 'Note Intervento' : 'Input Vocale'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Stato ascolto animato */}
            {isListening && (
              <div className="text-center py-6">
                <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-cyan-500 flex items-center justify-center animate-pulse">
                  <Mic className="h-8 w-8 text-white" />
                </div>
                <p className="text-lg font-medium text-cyan-600">Sto ascoltando...</p>
              </div>
            )}

            {/* Area testo MODIFICABILE */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-mono uppercase">Testo (modifica se necessario):</Label>
                <span className="text-xs text-muted-foreground">
                  {voiceText.length} caratteri
                </span>
              </div>
              <Textarea
                value={voiceText}
                onChange={(e) => setVoiceText(e.target.value)}
                placeholder={voiceMode === 'nota' 
                  ? "Parla o scrivi la nota..." 
                  : "Parla o scrivi la descrizione dell'intervento..."
                }
                className="rounded-none min-h-[120px] text-base"
                data-testid="voice-text-input"
              />
            </div>

            {/* Pulsanti azione */}
            <div className="flex gap-2">
              {isListening ? (
                <Button 
                  onClick={handleStopVoice} 
                  className="flex-1 h-12 rounded-none bg-red-500 hover:bg-red-600"
                >
                  <MicOff className="h-5 w-5 mr-2" />
                  Ferma Registrazione
                </Button>
              ) : (
                <Button 
                  onClick={restartVoice}
                  variant="outline"
                  className="flex-1 h-12 rounded-none border-cyan-500 text-cyan-600"
                >
                  <Mic className="h-5 w-5 mr-2" />
                  {voiceText ? 'Aggiungi con Voce' : 'Inizia Registrazione'}
                </Button>
              )}
            </div>

            <DialogFooter className="flex gap-2 sm:gap-2">
              <Button 
                variant="outline" 
                onClick={cancelVoiceInput} 
                className="flex-1 rounded-none"
              >
                Annulla
              </Button>
              <Button 
                onClick={confirmVoiceInput}
                disabled={!voiceText.trim()}
                className="flex-1 rounded-none bg-cyan-500 hover:bg-cyan-600"
              >
                {voiceMode === 'nota' ? 'Salva Nota' : 'Compila Form Intervento'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
